import { US_STATES, MX_STATES, MX_CITIES, US_CITIES, PHONE_REGEX_MX, PHONE_REGEX_US, PLATFORM_CATEGORY_MAP } from "./Constants"

export function getStatesByCountry(countryCode) {
    const statesMap = {
        US: US_STATES,
        MX: MX_STATES,
    }

    if (countryCode === "ALL") {
        return Object.entries(statesMap).flatMap(([country, stateList]) =>
            Object.entries(stateList).map(([stateCode, stateName]) => ({
                value: stateCode,
                label: `${country} - ${stateCode} - ${stateName}`,
            }))
        )
    }

    return statesMap[countryCode] || US_STATES
}

export function getStateOptions(states) {
    return Object.keys(states).map((stateCode) => ({
        value: stateCode,
        label: `${stateCode} - ${states[stateCode]}`,
    }))
}

export function getCitiesByState(stateCode, countryCode) {
    const citiesMap = {
        US: US_CITIES,
        MX: MX_CITIES,
    }

    const statesMap = {
        US: US_STATES,
        MX: MX_STATES,
    }

    const stateName = statesMap[countryCode] && statesMap[countryCode][stateCode]
    return stateName && citiesMap[countryCode] && citiesMap[countryCode][stateName]
        ? citiesMap[countryCode][stateName]
        : []
}

export function getCityOptions(cities) {
    return cities.map((city) => ({
        value: city,
        label: city,
    }))
}

export const validatePhone = (phone, branchCountry) => {
    let isValid = false
    let errorMessage = ""

    if (branchCountry === "US") {
        isValid = PHONE_REGEX_US.test(phone)
        errorMessage = "Invalid phone number. Use +1 format, e.g., +19171230000."
    } else if (branchCountry === "MX") {
        isValid = PHONE_REGEX_MX.test(phone)
        errorMessage = "Invalid phone number. Use +52 format, e.g., +525551234567."
    } else {
        errorMessage = `Invalid phone number format. The provided country code '${branchCountry}' is not supported.`
    }

    return {
        isValid,
        errorMessage: isValid ? null : errorMessage,
    }
}

export const getPhonePlaceholder = (branchCountry) => {
    if (branchCountry === "US") {
        return "e.g., +19171230000."
    } else if (branchCountry === "MX") {
        return "e.g., +525551234567."
    }
    return "Phone"
}

export const getPhoneMaxLength = (branchCountry) => {
    if (branchCountry === "US") {
        return 12
    } else if (branchCountry === "MX") {
        return 13
    }
    return 15
}

export const getCategoryByPlatform = (platformCode) => {
    const platform = platformCode || ""
    const categoryOptions = PLATFORM_CATEGORY_MAP[platform] || []
    const mappedOptions = categoryOptions.map(item => ({
        label: item,
        value: item
    }))
    return mappedOptions
}

// Formats Date object to YYYY-MM-DD
export const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// Formats date string to long format (e.g., "Dec 1, 2025")
export const formatDateLong = (dateString) => {
    const date = new Date(dateString + 'T00:00:00')
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Validates YYYY-MM-DD date string
export const isValidDate = (dateString) => {
    if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return false
    const date = new Date(dateString + 'T00:00:00')
    return date instanceof Date && !isNaN(date)
}

// Converts UTC timestamp to YYYY-MM-DD in specified timezone
export const convertToTimezoneDate = (utcTimestamp, timezone) => {
    try {
        const date = new Date(utcTimestamp)
        const formatter = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone || 'UTC',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        })
        return formatter.format(date)
    } catch (error) {
        const date = new Date(utcTimestamp)
        return formatDate(date)
    }
}

const MINUTES_PER_HOUR = 60
const MINUTES_PER_DAY = 1440

export const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined || minutes < 0 || isNaN(minutes)) {
        return '--'
    }

    if (minutes < MINUTES_PER_HOUR) {
        return `${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`
    } else if (minutes < MINUTES_PER_DAY) {
        // Less than 24 hours: show hours and minutes
        const hours = Math.floor(minutes / MINUTES_PER_HOUR)
        const remainingMinutes = minutes % MINUTES_PER_HOUR

        if (remainingMinutes > 0) {
            const hourText = hours === 1 ? 'Hour' : 'Hours'
            const minuteText = remainingMinutes === 1 ? 'Minute' : 'Minutes'
            return `${hours} ${hourText} ${remainingMinutes} ${minuteText}`
        } else {
            return `${hours} ${hours === 1 ? 'Hour' : 'Hours'}`
        }
    } else {
        // 24+ hours: show days
        const days = Math.floor(minutes / MINUTES_PER_DAY)
        return `${days} ${days === 1 ? 'Day' : 'Days'}`
    }
}
